/**
 * BMA360D DSP selection and bundle matching (PIG-516 / WDT-76 — "BMA360D
 * Equipment Selection Logic" §3 and §4).
 *
 * §3 DSP selection: the DSP is sized by the total AEC-channel requirement across
 * all BMA360D units in the room. Beam count per unit is driven by how far the
 * mic must reach to the furthest talker (the "slant distance" to the edge of the
 * coverage box). A single unit uses 8 or 12 beams by slant distance; two-or-more
 * units always use 8 beams each, and the minimum-cost DSP combination (fewer,
 * larger units) is selected.
 *
 * §4 bundle matching: a room matches a BMA360D bundle only when it has a single
 * BMA360D unit, C-IC6 speakers, and the speaker quantity exactly matches the
 * bundle (2 for the X800/medium bundle, 4 for the X1600/large bundle). Anything
 * else falls through to à la carte.
 *
 * Pure logic — no Redux/state dependency. The coverage box is derived from the
 * same room/table geometry the placement algorithm uses (mirrors
 * `getCoverageBoxes` in roomCalculations.ts) so DSP selection agrees with mic
 * placement.
 */

import { find } from 'lodash';
import equipmentCatalog from 'globalConstants/EquipmentCatalog';

/** Slant-distance threshold (m): ≤ → 8 beams, > → 12 beams (single unit). */
export const BEAM_THRESHOLD = 3.5;
/** Reference talker height (m), subtracted from ceiling height for the drop. */
export const TALKER_HEIGHT = 1.2;
/** AEC channels per BMA360D unit when the room has two or more units. */
export const MULTI_UNIT_AEC_PER_UNIT = 8;

/** AEC channels each Forte-X DSP model provides, largest first (cost packing). */
const DSP_TIERS = [
  { model: 'TesiraFORTÉ X 1600', aec: 16 },
  { model: 'TesiraFORTÉ X 800', aec: 8 },
  { model: 'TesiraFORTÉ X 400', aec: 4 },
] as const;

type DspTier = { model: string; aec: number };

interface AreaLike {
  dimensions?: { length?: number; width?: number };
  ceilingHeight?: { min?: number; max?: number } | number;
  table?: { shape?: string | null; size?: { length?: number; width?: number } };
  microphones?: { coverage?: { focused?: boolean; full_room?: boolean } };
}

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/** Ceiling height in metres from an area (`{min,max}` or scalar), 0 if absent. */
export const getAreaCeilingHeight = (area: AreaLike | undefined): number => {
  const ch = area?.ceilingHeight;
  return typeof ch === 'number' ? num(ch) : num(ch?.min);
};

/**
 * Longest dimension of the coverage box the placement algorithm targets.
 * Focused (table present + user selects focused): table dimensions + a
 * `table2wall` margin on the occupied sides (or 70% of the room when no table).
 * Otherwise (full-room): the room dimensions. Mirrors `getCoverageBoxes`.
 */
export const getCoverageBoxLongest = (area: AreaLike | undefined, constants: any): number => {
  const dims = area?.dimensions ?? {};
  const roomLen = num(dims.length);
  const roomWid = num(dims.width);
  const focused = area?.microphones?.coverage?.focused === true;

  if (!focused) {
    return Math.max(roomLen, roomWid);
  }

  const table2wall = num(constants?.area?.table2wall);
  const table = area?.table;
  if (table?.shape) {
    const endTable = constants?.tables?.[table.shape]?.endTable ?? false;
    const len = num(table.size?.length) + table2wall * (endTable ? 1 : 2);
    const wid = num(table.size?.width) + table2wall * 2;
    return Math.max(len, wid);
  }
  // Focused with no table → 70% of room (matches getCoverageBoxes).
  return Math.max(roomLen * 0.7, roomWid * 0.7);
};

/**
 * Slant distance (m) from a ceiling-tile mic to the furthest talker at the edge
 * of the coverage box. √(horizontalReach² + verticalDrop²).
 */
export const computeSlantDistance = (coverageBoxLongest: number, ceilingHeight: number): number => {
  const horizontalReach = num(coverageBoxLongest) / 2;
  const verticalDrop = Math.max(0, num(ceilingHeight) - TALKER_HEIGHT);
  return Math.sqrt(horizontalReach ** 2 + verticalDrop ** 2);
};

/**
 * Total AEC channels required across all BMA360D units in the room (§3).
 * One unit: 8 beams (≤ threshold) or 12 beams (> threshold). Two-or-more units:
 * 8 beams each. A room with no placed unit yet still needs a base DSP (8).
 */
export const getBma360dAecChannels = (
  unitCount: number,
  coverageBoxLongest: number,
  ceilingHeight: number,
): number => {
  const units = Math.max(0, Math.floor(num(unitCount)));
  if (units <= 1) {
    const slant = computeSlantDistance(coverageBoxLongest, ceilingHeight);
    return slant <= BEAM_THRESHOLD ? 8 : 12;
  }
  return units * MULTI_UNIT_AEC_PER_UNIT;
};

/**
 * Minimum-cost DSP combination for a total AEC-channel requirement (§3).
 * Packs with X1600 first, then a single X800/X400 to cover the remainder, so
 * fewer, larger units are preferred (e.g. 24ch → 1× X1600 + 1× X800, not 3× X800;
 * the single-unit 12ch case → 1× X1600). Returns `[tier, qty]` tuples.
 */
export const selectBma360dDspTiers = (aecChannels: number): Array<[DspTier, number]> => {
  const [x1600, x800, x400] = DSP_TIERS;
  let remaining = Math.max(0, Math.ceil(num(aecChannels)));
  const counts: Record<string, number> = {};

  counts[x1600.model] = Math.floor(remaining / x1600.aec);
  remaining -= counts[x1600.model] * x1600.aec;
  if (remaining > x800.aec) {
    counts[x1600.model] += 1; // 9–15 leftover (single-unit 12ch) → another X1600
  } else if (remaining > x400.aec) {
    counts[x800.model] = (counts[x800.model] ?? 0) + 1; // 5–8 leftover → X800
  } else if (remaining > 0) {
    counts[x400.model] = (counts[x400.model] ?? 0) + 1; // 1–4 leftover → X400
  }

  return DSP_TIERS
    .filter(tier => (counts[tier.model] ?? 0) > 0)
    .map(tier => [tier as DspTier, counts[tier.model]] as [DspTier, number]);
};

/**
 * BMA360D DSP catalog entries for the à la carte path, as `[processor, qty]`
 * tuples (same shape as `getProcessors` in logic.js). Returns `[]` when no
 * catalog match is found so the caller can fall back.
 */
export const getBma360dProcessors = (
  unitCount: number,
  coverageBoxLongest: number,
  ceilingHeight: number,
): Array<[any, number]> => {
  const aec = getBma360dAecChannels(unitCount, coverageBoxLongest, ceilingHeight);
  return selectBma360dDspTiers(aec)
    .map(([tier, qty]) => {
      const proc = find(equipmentCatalog.Processors, (p: any) => p.modelName === tier.model);
      return proc ? ([proc, qty] as [any, number]) : null;
    })
    .filter(Boolean) as Array<[any, number]>;
};

/** Single-unit DSP model used to anchor the bundle (X800 for 8ch, X1600 for 12ch). */
export const getBma360dSingleUnitDspModel = (
  coverageBoxLongest: number,
  ceilingHeight: number,
): string => {
  const aec = getBma360dAecChannels(1, coverageBoxLongest, ceilingHeight);
  return aec <= 8 ? 'TesiraFORTÉ X 800' : 'TesiraFORTÉ X 1600';
};

/** True for the base Desono C-IC6 speaker (excludes LP / TAA variants). */
const isBaseCIC6 = (spkrModel?: string): boolean =>
  !!spkrModel && spkrModel.includes('C-IC6') && !spkrModel.includes('LP');

/** True for the 600mm BMA360D-I variant (vs the 24" BMA360D). */
const isBma360dIVariant = (micModel?: string): boolean =>
  !!micModel && micModel.includes('BMA360D-I');

interface Bma360dBundleQuery {
  micModel?: string;
  unitCount: number;
  spkrModel?: string;
  spkrQty: number;
  coverageBoxLongest: number;
  ceilingHeight: number;
}

/**
 * Match a BMA360D bundle (§4). All conditions must hold simultaneously:
 *   - exactly one BMA360D unit,
 *   - C-IC6 speakers,
 *   - DSP anchors the bundle size (X800 → medium, X1600 → large),
 *   - speaker quantity is at least the bundle's own count (2 medium / 4 large).
 * A room with MORE speakers than the bundle ships with still gets the bundle —
 * the caller is responsible for retaining it and adding the extra speakers (and
 * their CCA/AMP-450BP/BPAK/cabling) as excess on top, the same way a TCM-X
 * bundle absorbs overflow (support report: user adds speakers on top of an
 * already-matched BMA360D bundle — the bundle must not be discarded). A room
 * with FEWER speakers than the bundle ships with can't use it at all (you can't
 * under-fill a fixed-content SKU), so that still returns `null` → full à la carte.
 */
export const matchBma360dBundle = ({
  micModel,
  unitCount,
  spkrModel,
  spkrQty,
  coverageBoxLongest,
  ceilingHeight,
}: Bma360dBundleQuery): any | null => {
  if (Math.floor(num(unitCount)) !== 1) return null;
  if (!isBaseCIC6(spkrModel)) return null;

  const dspModel = getBma360dSingleUnitDspModel(coverageBoxLongest, ceilingHeight);
  const isLarge = dspModel.includes('1600');
  const expectedSpkrQty = isLarge ? 4 : 2;
  if (num(spkrQty) < expectedSpkrQty) return null;

  const variant = isBma360dIVariant(micModel) ? 'BMA360DI' : 'BMA360D';
  const size = isLarge ? 'L-X1600' : 'M-X800';
  // Match on partNumber (the bare SKU) rather than modelName — modelName is the
  // customer-facing display string ("Biamp MRB-...", PIG-546) while partNumber
  // stays the stable, unprefixed identifier this lookup is built from.
  const partNumber = `MRB-${size}-${variant}-CIC6`;
  return find(equipmentCatalog.Bundles, (b: any) => b.partNumber === partNumber) ?? null;
};
